import React, { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import CryptoJS from "crypto-js";
import {
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
} from "react-icons/fa";

/**
 * CustomModal - small professional modal component
 * Props:
 *  - open (bool)
 *  - onClose (fn)
 *  - title (string)
 *  - message (string or JSX)
 *  - type: "success" | "warning" | "error" | "info"
 *  - showCancel (bool)
 *  - onConfirm (fn)
 *  - confirmText (string)
 *  - autoClose (ms) optional -> modal will auto close after ms
 */
function CustomModal({
  open,
  onClose,
  title,
  message,
  type = "info",
  showCancel = false,
  onConfirm = null,
  confirmText = "OK",
  autoClose = 0,
}) {
  React.useEffect(() => {
    if (!open) return;
    let t;
    if (autoClose && autoClose > 0) {
      t = setTimeout(() => {
        if (onClose) onClose();
      }, autoClose);
    }
    return () => clearTimeout(t);
  }, [open, autoClose, onClose]);

  if (!open) return null;

  const iconStyles = {
    success: {
      bg: "#e6fffa",
      color: "#06b6a4",
      icon: <FaCheckCircle size={36} />,
    },
    warning: {
      bg: "#137b871a",
      color: "#06b6a4",
      icon: <FaExclamationTriangle size={36} />,
    },
    error: {
      bg: "#fff5f5",
      color: "#ef4444",
      icon: <FaTimesCircle size={36} />,
    },
    info: { bg: "#eef2ff", color: "#6366f1", icon: <FaInfoCircle size={36} /> },
  }[type];

  return (
    <div
      aria-modal="true"
      role="dialog"
      className="custom-modal-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(16,24,32,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20,
      }}
    >
      <div
        className="custom-modal-card"
        style={{
          width: "100%",
          maxWidth: 560,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 14px 40px rgba(2,6,23,0.2)",
          overflow: "hidden",
          transform: "translateY(0)",
          animation: "modalPop .18s ease",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 20,
            padding: 22,
            alignItems: "center",
          }}
        >
          <div
            style={{
              minWidth: 72,
              minHeight: 72,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: iconStyles.bg,
              color: iconStyles.color,
            }}
          >
            {iconStyles.icon}
          </div>

          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>
              {title}
            </h3>
            <div
              style={{
                marginTop: 8,
                color: "#334155",
                fontSize: 15,
                lineHeight: 1.45,
              }}
            >
              {message}
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: showCancel ? "space-between" : "flex-end",
            gap: 12,
            padding: 16,
            borderTop: "1px solid rgba(15,23,42,0.04)",
            background: "#fafafa",
          }}
        >
          {showCancel && (
            <button
              onClick={onClose}
              style={{
                background: "#fff",
                border: "1px solid rgba(15,23,42,0.06)",
                padding: "10px 16px",
                borderRadius: 10,
                cursor: "pointer",
                fontWeight: 600,
                color: "#111827",
              }}
            >
              Cancel
            </button>
          )}

          {/* {confirmText && (
            <button
              onClick={() => {
                if (onConfirm) onConfirm();
                else onClose();
              }}
              style={{
                background: type === "success" ? "#06b6a4" : "#0f172a",
                color: "#fff",
                border: "none",
                padding: "10px 18px",
                borderRadius: 10,
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              {confirmText}
            </button>
          )} */}
        </div>
      </div>

      <style>{`
        @keyframes modalPop {
          from { transform: translateY(8px) scale(.99); opacity: 0 }
          to { transform: translateY(0) scale(1); opacity: 1 }
        }
      `}</style>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false); // track form submission
  const [loading, setLoading] = useState(false); // disable button during API
  const navigate = useNavigate();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPayload, setModalPayload] = useState({
    title: "",
    message: "",
    type: "info",
    showCancel: false,
    onConfirm: null,
    confirmText: "OK",
    autoClose: 0,
  });

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/css/style.css";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // helper to open modal
  const openModal = (opts) => {
    setModalPayload((p) => ({ ...p, ...opts }));
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  // Email validation function
  const validateEmail = (value) => {
    const v = (value || "").trim();
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!v) return "Email is required";
    if (!regex.test(v)) return "Please enter a valid email address";
    return "";
  };

  // Password validation
  const validatePassword = (value) => {
    const v = value || "";
    if (!v) return "Password is required";
    return "";
  };

  // Called only on submit to show required / invalid messages
  const runFullValidation = () => {
    const eErr = validateEmail(email);
    const pErr = validatePassword(password);
    setEmailError(eErr);
    setPasswordError(pErr);

    // focus first invalid field
    if (eErr) {
      emailRef.current?.focus();
      return false;
    }
    if (pErr) {
      passwordRef.current?.focus();
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    // run validation
    if (!runFullValidation()) {
      // show modal + inline messages
      // openModal({
      //   title: "Validation error",
      //   message: (
      //     <>
      //       Please fix the highlighted fields.
      //       <div style={{ marginTop: 8 }}>
      //         {emailError && <div>- {emailError}</div>}
      //         {passwordError && <div>- {passwordError}</div>}
      //       </div>
      //     </>
      //   ),
      //   type: "warning",
      //   confirmText: "OK",
      // });

      openModal({
  title: "Validation error",
  message: "Please fix the highlighted fields.",
  type: "warning",
  // confirmText: "OK",   // button visible
  autoClose: 3000,     // closes even if user doesn't click
});

      return;
    }

    // everything ok → call API
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/login`,
        {
          email: email.trim(),
          password,
        },
      );

      if (res.status === 200) {
        const encryptedData = CryptoJS.AES.encrypt(
          JSON.stringify(res.data.data),
          process.env.REACT_APP_LOGIN_SEC,
        ).toString();
        localStorage.setItem(
          process.env.REACT_APP_LOGIN_DATA_STORE,
          encryptedData,
        );
        // Store JWT so agent dashboard and other API calls can send it (cross-origin cookies may not be sent)
        if (res.data.token) {
          localStorage.setItem("authToken", res.data.token);
        }

        // Show a nice success modal and then navigate on confirm
        const isAdmin = res.data.data.role_name === "Admin";
        const isAdvisor = res.data.data.role_name === "Advisor";
        openModal({
          title: isAdmin ? "Admin Login Successfully" : "Login Successfully",
          message: isAdmin
            ? "Welcome, Admin. Redirecting to dashboard..."
            : "Welcome. Redirecting to your dashboard...",
          type: "success",
          showCancel: false,
          confirmText: "", // ❌ no button text
          onConfirm: null, // ❌ no manual confirm
          autoClose: 1800, // ⏱ auto close modal
        });

        // ⏱ AUTO REDIRECT
        setTimeout(() => {
          if (isAdmin) {
            window.location.href = "/dashboard";
          } else if (isAdvisor) {
            window.location.href = "/agent/dashboard";
          } else {
            window.location.href = "/overview";
          }
        }, 1800);
      } else {
        // openModal({
        //   title: "Authentication Failed",
        //   message: "Wrong Email or Password.",
        //   type: "warning",
        //   confirmText: "Try Again",
        // });

        openModal({
          title: "Authentication Failed",
          message: "Wrong Email or Password.",
          type: "warning",
          confirmText: "",
          autoClose: 2000,
        });
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        // openModal({
        //   title: "Invalid Email or Password",
        //   message: "Please check your credentials and try again.",
        //   type: "warning",
        //   confirmText: "OK",
        // });
        openModal({
          title: "Invalid Email or Password",
          message: "Please check your credentials and try again.",
          type: "warning",
          confirmText: "", // ❌ no button
          autoClose: 2000, // ⏱ auto close in 2 sec
        });
      } else {
        console.error("Login Error:", err);
        // openModal({
        //   title: "Something went wrong",
        //   message: "Unable to login right now. Please try again later.",
        //   type: "error",
        //   confirmText: "OK",
        // });
        openModal({
          title: "Something went wrong",
          message: "Unable to login right now. Please try again later.",
          type: "error",
          confirmText: "",
          autoClose: 2500,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .error-text { color: #dc2626; font-size: 0.875rem; margin-top: 4px; }
        .error-input { border-color: #dc2626 !important; box-shadow: 0 0 0 3px rgba(220,38,38,0.06); }
        .btn-gps-primary {
          background: linear-gradient(90deg,#06b6a4,#06a59a);
          color: #fff;
          border: none;
          padding: 10px 16px;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
        }
        .btn-gps-primary[disabled] { opacity: 0.7; cursor: not-allowed; transform: none; }
.login-logo {
  max-width: 140px;
  transition: 0.3s ease;
   object-fit: contain;
}
.login-logo-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
}
.login-logo:hover {
  transform: scale(1.05);
}
      `}</style>

      <div
        className="sign-in__wrapper"
        style={{
          background:
            "linear-gradient(135deg, rgb(240 253 253) 0%, rgb(155 222 218 / 73%) 50%, rgb(3 186 187) 100%)",
          minHeight: "100vh",
          padding: "20px 0",
        }}
      >
        <div className="container-fluid">
          <div className="row align-items-center justify-content-center pt-5">
            <form onSubmit={handleSubmit}>
              <div
                className="col-lg-6 col-xl-5 mx-auto"
                style={{ width: "100%" }}
              >
                <div
                  className="card px-4 py-5 gps-card"
                  style={{
                    maxWidth: "500px",
                    margin: "0 auto",
                    borderRadius: 12,
                  }}
                >
                  {/* Logo */}
                  <div className="login-logo-wrapper">
                    <Link to="/">
                      <img
                        src="/images/gpslogo.png"
                        alt="GPS Logo"
                        className="login-logo"
                        style={{ cursor: "pointer" }}
                      />
                    </Link>
                  </div>
                  <div className="text-center mb-4">
                    <h3
                      className="text-gps-primary"
                      style={{ color: "#0f172a" }}
                    >
                      Welcome Back
                    </h3>
                    <p style={{ color: "#334155" }}>
                      Sign in to your GPS account
                    </p>
                  </div>

                  {/* Email Field */}
                  <div className="mb-4">
                    <label className="form-label text-gps-primary">
                      <strong>Email Address *</strong>
                    </label>
                    <input
                      ref={emailRef}
                      type="email"
                      name="email"
                      className={`form-control ${
                        submitted && emailError ? "error-input" : ""
                      }`}
                      placeholder="Enter your email address"
                      id="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError("");
                      }}
                      onBlur={() => {
                        if (submitted) setEmailError(validateEmail(email));
                      }}
                      style={{
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(2,6,23,0.08)",
                      }}
                    />
                    {submitted && emailError && (
                      <div className="error-text">{emailError}</div>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="mb-4 position-relative">
                    <label className="form-label text-gps-primary">
                      <strong>Password *</strong>
                    </label>
                    <input
                      ref={passwordRef}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className={`form-control ${
                        submitted && passwordError ? "error-input" : ""
                      }`}
                      placeholder="Enter your password"
                      id="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError("");
                      }}
                      onBlur={() => {
                        if (submitted)
                          setPasswordError(validatePassword(password));
                      }}
                      style={{
                        paddingRight: "44px",
                        padding: "10px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(2,6,23,0.08)",
                      }}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        paddingBottom: "42px",
                        right: "12px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#000",
                      }}
                      aria-hidden
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                    {submitted && passwordError && (
                      <div className="error-text">{passwordError}</div>
                    )}
                  </div>

                  <div className="mb-4 text-end">
                    <Link
                      to={"/forgot-password"}
                      style={{ textDecoration: "none", color: "#0f766e" }}
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="btn-gps-primary w-100 py-2 mb-3"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "instant" })
                    }
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Login"}
                  </button>

                  <div className="text-center">
                    <p className="mb-2" style={{ color: "#334155" }}>
                      Don't have an account?
                    </p>

                    <Link
                      to={"/AdvisorRegistration"}
                      onClick={() =>
                        window.scrollTo({ top: 0, behavior: "instant" })
                      }
                    >
                      <button
                        type="button"
                        className="btn-gps-primary"
                       
                      >
                        Create Account
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Modal */}
        <CustomModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={modalPayload.title}
          message={modalPayload.message}
          type={modalPayload.type}
          showCancel={modalPayload.showCancel}
          onConfirm={() => {
            if (modalPayload.onConfirm) modalPayload.onConfirm();
            else setModalOpen(false);
          }}
          confirmText={modalPayload.confirmText}
          autoClose={modalPayload.autoClose}
        />
      </div>
    </>
  );
}
